import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Zap, Sparkles, Crown, ArrowRight } from 'lucide-react';
import { getCTAClasses, CTA_MESSAGES } from '@/lib/cta-config';

interface PricingTierProps {
  name: string;
  monthlyPrice: number;
  annualPrice: number;
  period: string;
  description: string;
  valueProposition: string;
  features: string[];
  highlighted?: boolean;
  buttonText: string;
  onGetStarted?: () => void;
  isAnnual: boolean;
  badge?: string;
  icon?: React.ReactNode;
}

const PricingTier: React.FC<PricingTierProps> = ({
  name,
  monthlyPrice,
  annualPrice,
  period,
  description,
  valueProposition,
  features,
  highlighted = false,
  buttonText,
  onGetStarted,
  isAnnual,
  badge,
  icon,
}) => {
  const currentPrice = isAnnual ? annualPrice : monthlyPrice;
  const savings = Math.round(((monthlyPrice * 12 - annualPrice) / (monthlyPrice * 12)) * 100);

  return (
    <motion.div
      className={`relative rounded-2xl transition-all duration-500 flex flex-col h-full ${
        highlighted 
          ? 'bg-gradient-to-b from-emerald-50 via-white to-cyan-50 border-2 border-emerald-200 transform scale-105 z-10' 
          : 'bg-white border-2 border-gray-300 hover:border-emerald-200 hover:shadow-lg'
      }`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true }}
      whileHover={{ y: -8, scale: highlighted ? 1.05 : 1.02 }}
    >
      {/* Popular badge */}
      {badge && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-20">
          <motion.div
            className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center shadow-xl border-2 border-white"
            initial={{ scale: 0, rotate: -10 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.3, type: 'spring', bounce: 0.5 }}
          >
            <Crown className="w-3 h-3 mr-1.5" />
            {badge}
          </motion.div>
        </div>
      )}

      {/* Animated background gradient */}
      {highlighted && (
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 via-cyan-500/5 to-emerald-500/5 rounded-2xl"
          animate={{
            backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'linear',
          }}
          style={{
            backgroundSize: '200% 200%',
          }}
        />
      )}

      <div className="relative z-10 p-6 flex flex-col h-full">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center mb-3">
            {icon && (
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mr-3 ${
                highlighted 
                  ? 'bg-gradient-to-r from-emerald-500 to-cyan-600 text-white' 
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {icon}
              </div>
            )}
            <h3 className="text-xl font-bold text-gray-900">{name}</h3>
          </div>

          {/* Price display */}
          <div className="mb-4">
            <div className="flex items-center justify-center mb-2">
              <span className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-cyan-600 bg-clip-text text-transparent">
                ${currentPrice === 0 ? '0' : currentPrice}
              </span>
              {currentPrice > 0 && (
                <span className="text-gray-600 ml-2 text-base">/{period}</span>
              )}
            </div>
            
            {/* Savings indicator */}
            {isAnnual && currentPrice > 0 && savings > 0 && (
              <motion.div
                className="inline-flex items-center bg-gradient-to-r from-emerald-100 to-cyan-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-semibold"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.5, type: 'spring' }}
              >
                <Sparkles className="w-4 h-4 mr-1" />
                Save {savings}% annually
              </motion.div>
            )}
            
            {/* Cross-out monthly price for annual */}
            {isAnnual && currentPrice > 0 && (
              <div className="text-sm text-gray-500 mt-2">
                <span className="line-through">${monthlyPrice * 12}/year</span>
              </div>
            )}
          </div>

          {/* Value proposition */}
          <div className="mb-4">
            <p className={`text-base font-semibold mb-2 ${
              highlighted 
                ? 'text-emerald-700' 
                : 'text-gray-700'
            }`}>
              {valueProposition}
            </p>
            <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
          </div>
        </div>

        {/* Features list */}
        <div className="flex-grow mb-6">
          <ul className="space-y-3">
            {features.map((feature, index) => (
              <motion.li
                key={index}
                className="flex items-start"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.1 + index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className={`w-4 h-4 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0 ${
                  highlighted 
                    ? 'bg-emerald-100 text-emerald-600' 
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  <Check className="w-2.5 h-2.5" />
                </div>
                <span className="text-gray-700 text-sm leading-relaxed">{feature}</span>
              </motion.li>
            ))}
          </ul>
        </div>

        {/* CTA Button - This will be pushed to bottom by flex-grow above */}
        <div className="mt-auto">
          <motion.button
            onClick={onGetStarted}
            className={
              highlighted
                ? getCTAClasses('primary', 'w-full')
                : getCTAClasses('outline', 'w-full')
            }
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <span className="relative z-10">{buttonText}</span>
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

interface PricingSectionProps {
  onGetStarted?: () => void;
}

export const PricingSection: React.FC<PricingSectionProps> = ({ onGetStarted }) => {
  const [isAnnual, setIsAnnual] = useState(false);

  const pricingTiers = [
    {
      name: 'Free Plan',
      monthlyPrice: 0,
      annualPrice: 0,
      period: '',
      description: 'Perfect for single-store owners who want to try our forecasting.',
      valueProposition: 'Get Your Next Payment Forecast—Free',
      features: [
        '1-week payment forecast',
        'Weekly email notifications',
        'Upload up to 12 weeks of historical data',
        'Basic dashboard access',
        'Community support',
      ],
      buttonText: CTA_MESSAGES.trial,
      highlighted: false,
      icon: <Zap className="w-6 h-6" />,
    },
    {
      name: 'Pro Plan',
      monthlyPrice: 199,
      annualPrice: 1990, // 16% savings
      period: 'month',
      description: 'Ideal for independent owners who need reliable cash flow management.',
      valueProposition: 'Never Miss a Lottery Payment Again',
      features: [
        '4-week rolling forecast with 95% accuracy',
        'Full web dashboard experience',
        'Real-time high-payment alerts',
        'Event flagging (new book activations, holidays)',
        'Advanced analytics & insights',
        'Priority email support',
        'Export to CSV/Excel',
        'Mobile-responsive dashboard',
      ],
      buttonText: CTA_MESSAGES.proPlan,
      highlighted: true,
      badge: 'Most Popular',
      icon: <Crown className="w-5 h-5" />,
    },
  ];

  return (
    <section id="pricing" className="py-24 bg-gradient-to-b from-gray-50 via-white to-gray-50 relative overflow-hidden">
      {/* Animated Background decorations */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Floating animated circles */}
        <motion.div 
          className="absolute -top-40 -right-40 w-80 h-80 bg-emerald-500/5 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div 
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-cyan-500/5 rounded-full blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.6, 0.3, 0.6],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        
        {/* Additional floating elements */}
        <motion.div 
          className="absolute top-1/4 left-1/4 w-32 h-32 bg-emerald-300/10 rounded-full blur-2xl"
          animate={{
            x: [0, 50, 0],
            y: [0, -30, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div 
          className="absolute top-3/4 right-1/4 w-24 h-24 bg-cyan-300/10 rounded-full blur-2xl"
          animate={{
            x: [0, -40, 0],
            y: [0, 20, 0],
            scale: [1.1, 1, 1.1],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        
        {/* Gradient overlay with animation */}
        <motion.div 
          className="absolute inset-0 bg-gradient-to-br from-emerald-50/20 via-transparent to-cyan-50/20"
          animate={{
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        
        {/* Subtle moving particles */}
        <div className="absolute inset-0">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className={`absolute w-2 h-2 bg-emerald-200/30 rounded-full ${i % 2 === 0 ? 'bg-emerald-200/30' : 'bg-cyan-200/30'}`}
              style={{
                left: `${20 + (i * 15)}%`,
                top: `${30 + (i * 10)}%`,
              }}
              animate={{
                y: [0, -100, 0],
                opacity: [0, 1, 0],
                scale: [1, 1.5, 1],
              }}
              transition={{
                duration: 8 + i * 2,
                repeat: Infinity,
                ease: "easeInOut",
                delay: i * 1.5,
              }}
            />
          ))}
        </div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
        >
          <motion.h2 
            className="text-5xl md:text-6xl font-bold text-gray-900 mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Simple,{' '}
            <span className="bg-gradient-to-r from-emerald-600 via-cyan-600 to-emerald-600 bg-clip-text text-transparent">
              Transparent
            </span>{' '}
            Pricing
          </motion.h2>
          <motion.p 
            className="text-xl text-gray-600 max-w-3xl mx-auto mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            Choose the plan that fits your business. Start free, upgrade when you&apos;re ready to unlock the full potential of predictive analytics.
          </motion.p>

          {/* Billing toggle */}
          <motion.div
            className="flex items-center justify-center mb-8"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="bg-white rounded-xl p-1.5 shadow-lg border border-gray-200">
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => setIsAnnual(false)}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 ${
                    !isAnnual
                      ? 'bg-gradient-to-r from-emerald-500 to-cyan-600 text-white shadow-md'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setIsAnnual(true)}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 relative ${
                    isAnnual
                      ? 'bg-gradient-to-r from-emerald-500 to-cyan-600 text-white shadow-md'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Annual
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full font-bold">
                    Save 16%
                  </span>
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Pricing grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-4xl mx-auto mb-16 items-start pt-4">
          {pricingTiers.map((tier) => (
            <PricingTier
              key={tier.name}
              {...tier}
              onGetStarted={onGetStarted}
              isAnnual={isAnnual}
            />
          ))}
        </div>

        {/* View Full Pricing Details CTA */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <p className="text-gray-600 mb-6">
            Need enterprise solutions or want to compare all features?
          </p>
          <motion.a
            href="/pricing"
            className={getCTAClasses('primary', 'inline-flex items-center')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {CTA_MESSAGES.viewPricing} & Enterprise Options
            <ArrowRight className="w-5 h-5 ml-2" />
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};